import { Checkbox, FormLabel, Thead, background, border, extendTheme } from '@chakra-ui/react'
import { color } from 'framer-motion'

const theme = extendTheme({
  colors: {
    brand: {
      50: '#e5fff4',
      100: '#b2ffe1',
      200: '#80ffcf',
      300: '#4dffbc',
      400: '#1affaa',
      500: '#00e691',
      600: '#00b36e',
      700: '#00804a',
      800: '#004d27',
      900: '#001a03'
    },
    primary:{
      100: '#F7E4CC',
      400: '#DD8B25',
      500: '#FC9B25'
    },
    greyCold200: '#B0BBC4',
    neutralDark: '#131313',
    greyNeutral100: '#F5F5F5',
    greyCold500: '#222B45',
    neutralTrasparentDark: 'rgba(143, 155, 179, 0.16)',
    greyCold100: '#E5EAED',
    NeutralLight: '#FCFCFC',
    greyWarm100: '#EDEDED',
    greyNeutral300: '#888888',
    greyCold300: '#7C8A97',
    greyWarm300: '#968C7C',
    redInput: '#BA0F31',
    blueInput: '#3182CE'
  },
  fonts: {
    heading: `'Work Sans', sans-serif`,
    body: `'Work Sans', sans-serif`
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: '600',
        fontSize: '16px',
        lineHeigth: '140%'
      },
      variants: {
        'primary': {
          borderRadius: '17px',
          bg: '#DD8B25',
          color: '#fff',
          width: 'full',
          gap:'10px',
          padding: '12px 16px',
          height:'50px',
          whiteSpace: "normal",
          wordWrap: "break-word",
          _hover: {
            bg: '#F2B468',
            _disabled: {
              bg: '#393939',
            }
          },
          _active: {
            bg: '#FC9B25',
          },
          _loading: {
            bg: '#DD8B25'
          },
          _disabled: {
            bg: '#393939'
          }
        },
        'refresh': {
          borderRadius: '17px',
          bg: '#DD8B25',
          color: '#fff',
          gap:'10px',
          minWidth: '150px',
          padding: '4px 8px',
          height:'50px',
          whiteSpace: "normal",
          wordWrap: "break-word",
          _hover: {
            bg: '#F2B468',
            _disabled: {
              bg: '#393939',
            }
          },
          _active: {
            bg: '#FC9B25',
          },
          _loading: {
            bg: '#DD8B25'
          },
          _disabled: {
            bg: '#393939'
          }
        },
        'login': {
          borderRadius: '17px',
          bg: '#DD8B25',
          color: 'greyCold100',
          width: 'full',
          gap:'10px',
          padding: '12px 16px',
          height:'50px',
          _hover: {
            bg: '#393939',
          },
          _active: {
            bg: '#2c2c2c',
          },
          _loading: {
            background: '#2c2c2c',
            color: 'greyCold100',
            _hover: {
              bg: '#2c2c2c',
            },
          }
        },
        'secondary': {
          borderRadius: '17px',
          border: 'solid 1px',
          borderColor: 'primary.400',
          bg: 'white',
          color: 'primary.400',
          width: 'full',
          gap:'10px',
          padding: '12px 16px',
          height:'50px',
          whiteSpace: "normal",
          wordWrap: "break-word",
          _hover: {
            bg: 'primary.100',
          },
          _active: {
            color: 'primary.500',
          },
          _loading: {
            bg: 'white'
          },
          _disabled: {
            color: '#888888',
            bg: 'white',
            borderColor: '#B9B9B9',
          }
        },
        'outline': {
          borderRadius: '17px',
          border: 'solid 1px',
          borderColor: 'black',
          bg: 'greyNeutral100',
          color: 'black',
          gap:'10px',
          padding: '12px 16px',
          whiteSpace: "normal",
          wordWrap: "break-word",
          _hover: {
            bg: 'primary.100',
          },
          _active: {
            color: 'primary.500',
          },
          _loading: {
            bg: 'white'
          },
          _disabled: {
            color: '#888888',
            bg: 'greyNeutral100',
            borderColor: '#B9B9B9',
          }
        },
        
      },
      defaultProps: {
        size: 'md',
        variant: 'solid-primary', 
      },
    },
    HStack: {
      variants: {
        'dashboardMenuItem': {
          spacing: '4',
          cursor: 'pointer',
          width: '232px',
          height: '61px',
          bg: '#FFFFFF',
          _hover: {
            bg: 'neutralDark'}
        }
      }
    },
    Radio: {
      variants: {
        primary: () => ({
          background: `neutralDark`,
          control: {
            _checked: {
              background: `primary.500`,
              color: "primary.500",
              borderColor: 'primary.500',
              _hover:{
                background: `primary.500`,
                borderColor: "primary.500",
              }
            },
            _focus: {
              boxShadow: 'none'
            }
          },
        }),
      },
      defaultProps: {
        variant: "primary",
      },
    },
    Checkbox: {
      variants: {
        primary: () => ({
          background: `neutralDark`,
          control: {
            _checked: {
              background: `primary.500`,
              color: "primary.500",
              borderColor: 'primary.500',
              _hover:{
                background: `primary.500`,
                borderColor: "primary.500",
              }
            },
            _focus: {
              boxShadow: 'none'
            }
          },
        }),
      }
    },
    Table: {
      baseStyle: {
        thead:{
          borderBottom:'solid grey'
        },
        th: {
          textTransform: 'capitalize',
          bg: 'white',
          color: '#787885',
          fontWeight: '600',
          padding: '10px, 8px, 10px, 8px',
          fontSize: '14px',
          lineHeight: '19.6px',
          heigth: '40px',
          textWrap: 'nowrap',
        },
        td: {
          fontWeight: '400',
          fontSize: '16px',
          lineHeight: '22.4px',
          
        },
      },
    },
    Text: {
      variants: {
        FormError: () => ({
          paddingTop: '4px',
          color: '#CD1E58',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px'
        }),
        Verifying: () => ({
          bg: '#C9D7F8',
          border:'1px solid #C9D7F8',
          borderRadius:'600px',
          textAlign: "center",
          alignItems: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#4476EF',
          fontWeight:'600',
          fontSize:'14px',
          lineHeight: '19.6px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Ok: () => ({
          bg: '#E3FFFE',
          border:'1px solid #E3FFFE',
          color: '#07837F',
          borderRadius:'600px',
          alignItems: 'center',
          padding: '4px 6px',
          gap: '8px',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          textAlign: 'center',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Active: () => ({
          bg: '#E3FFFE',
          border:'1px solid #E3FFFE',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#07837F',
          fontWeight:'600',
          fontSize:'14px',
          lineHeight: '19.6px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        DeletedUser: () => ({
          bg: '#FEE8E7',
          border:'1px solid #FEE8E7',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: "center",
          padding: '4px 6px',
          gap: '8px',
          color: '#BA0F31',
          fontWeight:'600',
          fontSize:'14px',
          lineHeight: '19.6px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Banned: () => ({
          bg: '#FEE8E7',
          border:'1px solid #FEE8E7',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: "center",
          padding: '4px 6px',
          gap: '8px',
          color: '#BA0F31',
          fontWeight:'600',
          fontSize:'14px',
          lineHeight: '19.6px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Deactivated: () => ({
          bg: '#EDEDF0',
          border:'1px solid #EDEDF0',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: "center",
          padding: '4px 6px',
          gap: '8px',
          color: '#4A4B57',
          fontWeight:'600',
          fontSize:'14px',
          lineHeight: '19.6px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Suspended: () => ({
          bg: '#FBF0D0',
          border:'1px solid #FBF0D0',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#E57349',
          fontWeight:'600',
          fontSize:'14px',
          lineHeight: '19.6px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Accepted: () => ({
          border:'1px solid #049894',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#049894',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        CancelledPreAuth: () => ({
          border:'1px solid #7C8A97',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#7C8A97',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        CancelledEvent: () => ({
          border:'1px solid #7C8A97',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#7C8A97',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Invited: () => ({
          border:'1px solid #7C8A97',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#7C8A97',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        CancelledUser: () => ({
          border:'1px solid #CD1E58',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#CD1E58',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Refused: () => ({
          border:'1px solid #CD1E58',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#CD1E58',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Concluded: () => ({
          border:'1px solid #FEE8E7',
          background: '#FEE8E7',
          color: '#CD1E58',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        CancelledAdmin: () => ({
          border:'1px solid #EDEDF0',
          backgroundColor: '#EDEDF0',
          borderRadius:'600px',
          alignItems: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#4A4B57',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          textAlign: 'center',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        CancelledNoShow: () => ({
          border:'1px solid #EDEDF0',
          backgroundColor: '#EDEDF0',
          borderRadius:'600px',
          alignItems: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#4A4B57',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          textAlign: 'center',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        CancelledCreator: () => ({
          border:'1px solid #EDEDF0',
          backgroundColor: '#EDEDF0',
          borderRadius:'600px',
          alignItems: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#4A4B57',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          textAlign: 'center',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Cancelled: () => ({
          border:'1px solid #EDEDF0',
          backgroundColor: '#EDEDF0',
          borderRadius:'600px',
          alignItems: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#4A4B57',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          textAlign: 'center',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Deleted: () => ({
          border:'1px solid #551715',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#551715',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        }),
        Pending: () => ({
          border:'1px solid #7C8A97',
          borderRadius:'600px',
          alignItems: 'center',
          textAlign: 'center',
          padding: '4px 6px',
          gap: '8px',
          color: '#7C8A97',
          fontWeight:'500',
          fontSize:'12px',
          lineHeight: '14px',
          minWidth: '120px',
          textTransform: 'capitalize'
        })}
    },
    Tabs: {
      variants: {
        'primary': {
          tab: {
            background: 'white',
            color: '#B0BBC4',
            width:'103px',
            height:'76px',
            borderRadius:'0px',
            textTransform:'uppercase',
            borderBottom: 'solid 4px',
            _selected: {
              background: 'white',
              borderBottom: 'solid 4px',
              color: '#4476EF'
            }
          },
          tabpanel: {
            padding: '4',
            borderRadius: '0px',
          },
        },
      }
    },
    Switch:{
      variants: {
        primary: () => ({
          track: {
            bg: 'rgba(143, 155, 179, 0.16)',
            _checked: {
              bg: 'primary.500',
            },
          },
          thumb: {
            bg: 'white',
          },
        })}
    },
    Select: {
      variants: {
        primary: () => ({
          field: {
            background: 'white',
            color: 'black',
            border: '1px solid',
            borderColor:'#E2E7F0'
          },
        }),
        primaryBlack: () => ({
          field: {
            background: 'white',
            color: 'black',
            _placeholder: { color: 'gray' },
            border: '1px solid',
            borderColor:'#E2E7F0'
          },
        })}
    },
    Input: {
      variants: {
        date: () => ({
          field: {
            background: 'white',
            color: 'greyCold200',
            border: '1px solid',
            borderColor:'#E2E7F0',
            _disabled: {
              color: '#greyCold200',
              bg: 'white',
              borderColor: '#E2E7F0',
              opacity: '1.0'
            }
          },
        }),
      search : () => ({
        field: {
          color: 'neutralDark',
          opacity: '1'
        },
      })}
    },
    FormLabel:{
      variants: {
        primary: () => ({
          color:'greyCold200',
          textTransform:'uppercase',
          fontWeight:'700',
          fontSize:'12px',
          lineHeight:'14px',
          marginBottom:'8px'
        })
      }
    }
  }
})

export default theme
